import { isValidEmail } from "@/utils/email";

import { TMessage } from "@/i18n/tt";
import { Language, TranslationDirection } from "@/i18n/languages";
import { TMessageKey } from "@/i18n/types.gen";
import { isProductionEnvironment } from "@/utils/environments";

import { isSwedishOrgNo } from "@/utils/orgNo";
import { DocumentType } from "./documents";
import { DecisionType } from "./decisions";

import { PaidSubscription, SubscriptionType } from "./subscription";

import { ShareTypeCode } from "./share";
import { PersonInfo } from "@/types/Shareholder";
import { compact } from "lodash";
import { ProductDescription } from "./product";
import { Recipient } from "@/dbot/common/commonDomain";
import {
  AccessRequestRecipients,
  NationalIdMaskingPolicy,
} from "@/enterpriseAccounts/enterpriseAccountsDomain";
import { CSSProperties } from "react";
import { CustomRoleItem } from "@/common/customRole.ts";

///
/// Result types
///
export type Ok<T = void> = { state: "ok"; data: T };
export type Failure<T = void> = { state: "failure"; error: T };
export type Pending = { state: "pending" };
export type Cancelled<T = void> = { state: "cancelled"; reason: T };
export type Result<T = void, TFailure = void, TCancelled = void> =
  | Ok<T>
  | Failure<TFailure>
  | Pending
  | Cancelled<TCancelled>;

export function ok(): Ok;
export function ok<T>(data: T): Ok<T>;
export function ok<T>(data?: T): Ok<T | void> {
  return { state: "ok", data };
}

export function cancelled(): Cancelled;
export function cancelled<T>(reason: T): Cancelled<T>;
export function cancelled<T>(reason?: T): Cancelled<T | void> {
  return {
    state: "cancelled",
    reason: reason,
  };
}

export function failure(): Failure;
export function failure<T>(error: T): Failure<T>;
export function failure<T>(error?: T): Failure<T | void> {
  return { state: "failure", error };
}

export const pending = (): Pending => ({ state: "pending" });

export const isOk = <T, F, C>(result?: Result<T, F, C>): result is Ok<T> => result?.state === "ok";
export const isFailure = <T, F, C>(result?: Result<T, F, C>): result is Failure<F> =>
  result?.state === "failure";
export const isPending = <T, F, C>(result?: Result<T, F, C>): result is Pending =>
  result?.state === "pending";
export const isCancelled = <T, F, C>(result?: Result<T, F, C>): result is Cancelled<C> =>
  result?.state === "cancelled";

///
/// validators
///
export const emailValidator = (value: string): TMessage | undefined => {
  if (value.trim().length === 0 || isValidEmail(value)) {
    return undefined;
  } else {
    return { key: "email-field.invalid-email" };
  }
};

export type Whitelabel = {
  id?: string;
  name: string;
  primaryColor: string;
  primaryContrastColor: string;
  primaryLightColor: string;
  primaryDarkColor: string;
  subDomain?: string;
  internalNotes?: string;
  logoId?: string;
};

///
/// Language
///

export type DocumentLanguage = "no" | "en" | "sv" | "no/en" | "sv/en";

export type TranslationOption = {
  autoTranslate: boolean;
  translationDirection?: TranslationDirection;
};

export type LocalizedText = {
  language: Language;
  text: string;
  translationOption?: TranslationOption;
};

export type LocalizedTextForDocument = {
  language: Language;
  documentType: DocumentType;
  text: string;
  translationOption?: TranslationOption;
};

export type SelectableWithId = {
  id: string;
  name: string;
  isSelected: boolean;
};

export type SubscriptionTypeModel = {
  currentSubscription: PaidSubscription | undefined;
  requiredSubscriptionType: SubscriptionType | undefined;
};

export enum FreetextType {
  DECISION_MATTER = "DECISION_MATTER",
  INFORMATION_MATTER = "INFORMATION_MATTER",
  CUSTOMIZED = "CUSTOMIZED",
}

export enum CustomizedFreetextType {
  DECISION_MATTER = "DECISION_MATTER",
  INFORMATION_MATTER = "INFORMATION_MATTER",
  EXCLUDED = "EXCLUDED",
}

export enum SpecialCircumstancesType {
  NONE = "NONE",
  NONE_UNKNOWN_TO_SUBSCRIBERS = "NONE_UNKNOWN_TO_SUBSCRIBERS",
  ADDED_EXPLANATION = "ADDED_EXPLANATION",
}

export enum VotingEligibilityOfShares {
  ALL = "ALL",
  SOME = "SOME",
  NONE = "NONE",
}

export enum ArticleChangeType {
  RemoveArticle = "RemoveArticle",
  EditArticle = "EditArticle",
  AddArticle = "AddArticle",
}

// noinspection JSUnusedGlobalSymbols
export enum SigningMethod {
  BANKID = "BANKID",
  BANKID_AUTH = "BANKID_AUTH",
  BANKID_SWEDEN_AUTH = "BANKID_SWEDEN_AUTH",
  MITID_DENMARK_AUTH = "MITID_DENMARK_AUTH",
  BANKID_FINLAND_AUTH = "BANKID_FINLAND_AUTH",
  AUTH_EMAIL = "AUTH_EMAIL",
  AUTH_SMS = "AUTH_SMS",
  AUTH_CALL = "AUTH_CALL",
  HANDWRITING = "HANDWRITING",
  SEAL = "SEAL",
}

export const signingMethodOptions = compact([
  SigningMethod.AUTH_EMAIL,
  SigningMethod.BANKID_AUTH,
  SigningMethod.BANKID_SWEDEN_AUTH,
  SigningMethod.MITID_DENMARK_AUTH,
  SigningMethod.BANKID_FINLAND_AUTH,
  SigningMethod.HANDWRITING,
]);

export function supportsBatchSigning(signingMethod: SigningMethod): boolean {
  return (
    signingMethod !== SigningMethod.BANKID &&
    signingMethod !== SigningMethod.BANKID_FINLAND_AUTH &&
    signingMethod !== SigningMethod.MITID_DENMARK_AUTH
  );
}

export type SignDocumentCount = {
  signingMethod: SigningMethod;
  count: number;
  company: CompanyItem;
};

// noinspection JSUnusedGlobalSymbols
export enum DecisionStatus {
  DRAFT = "DRAFT",
  PENDING_EXTERNAL_COMPLETION = "PENDING_EXTERNAL_COMPLETION",
  EXTERNAL_COMPLETED = "EXTERNAL_COMPLETED",
  COMPLETED = "COMPLETED",
}

export function signDocumentLabel(count: number): TMessage {
  if (count === 1) {
    return { key: "header.documentsToSign.signDocument" };
  } else {
    return { key: "header.documentsToSign.signDocuments", params: { count } };
  }
}

export function signingMethodLabel(signingMethod: SigningMethod): TMessageKey | undefined {
  switch (signingMethod) {
    case SigningMethod.BANKID:
    case SigningMethod.BANKID_AUTH:
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return "common.bankid";
    case SigningMethod.MITID_DENMARK_AUTH:
      return "header.documentsToSign.mitid";
    case SigningMethod.BANKID_FINLAND_AUTH:
      return "common.bankid-finland";
    case SigningMethod.HANDWRITING:
      return "header.documentsToSign.handwriting";
    case SigningMethod.AUTH_EMAIL:
      return "header.documentsToSign.email";
    default:
      return undefined;
  }
}

export const findDocumentLanguagesForOrgNo = (orgNo?: string): DocumentLanguage[] => {
  if (!isProductionEnvironment() && isSwedishOrgNo(orgNo)) {
    return ["sv", "en", "sv/en"];
  } else {
    return ["no", "en", "no/en"];
  }
};

export const requiredDocumentLanguagesForOrgNo = (orgNo?: string): DocumentLanguage[] => {
  if (isSwedishOrgNo(orgNo)) {
    return ["sv", "sv/en"];
  } else {
    return ["no", "no/en"];
  }
};

export const dualLanguageOptionForOrgNo = (orgNo: string): DocumentLanguage => {
  if (isSwedishOrgNo(orgNo)) {
    return "sv/en";
  } else {
    return "no/en";
  }
};

export type BR_STATUS =
  | "SENT_TO_BR"
  | "DELETED"
  | "SENT_FOR_SIGNING"
  | "PROCESSING"
  | "BR_APPROVED"
  | "REJECTED"
  | "NEEDS_DOCUMENTATION";

export type BR_DECISION_STATUS = BR_STATUS | "NOT_SENT";

export type CompanyItem = {
  organisationNumber: string;
  companyName: string;
};

export type RenderedDecisionRow = {
  cells: string[];
};

export enum SignerType {
  DOCUMENT = "DOCUMENT",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export type SignerId = number | string;

export interface PhoneNumber {
  countryCode?: string;
  value: string;
}

export interface Address {
  address?: string;
  addressId?: number;
  city?: string;
  country?: string;
  postalCode?: string;
  time_stamp?: string;
}

export type Signer = {
  id?: SignerId;
  reference?: string;
  name: string;
  email: string;
  phoneNumber?: PhoneNumber;
  signingMethod: SigningMethod;
  signerType: SignerType;
  language: Language;
  orgNo?: string;
  companyName?: string;
  documentId?: string;
  documentTitle?: string;
  documentGroupId?: string;
};

export type ProjectDocumentPreview = {
  documentId: string;
  orgNo: string;
  documentTitle: string;
  reference?: string;
};

export type GetProjectDocumentSignersResponse = {
  signers: Signer[];
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
  companySettings?: CompanySharepointSettingsForDocument[];
};

export type GetProjectDocumentRecipientsResponse = {
  recipients: Recipient[];
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
};

export type CompanySharepointSettingsForDocument = {
  orgNo: string;
  sharepointSettings?: SharepointSettingsForDocuments;
};

export type SharepointSettingsForDocuments = {
  settings: SharepointSettingsForDocument[];
};

export type SharepointSettingsForDocument = {
  destinationFolderId: string;
  driveId: string;
  destinationFolderName: string;
  documentType: DocumentType;
};

export function signer(data: Partial<Signer> = {}): Signer {
  return {
    ...data,
    id: data.id ?? undefined,
    name: data.name ?? "",
    email: data.email ?? "",
    signingMethod: data.signingMethod ?? SigningMethod.BANKID_AUTH,
    signerType: data.signerType ?? SignerType.DOCUMENT,
    language: data.language ?? Language.NO,
  };
}

export const mapToSigners = (s: Signer) => ({
  signerName: s.name,
  email: s.email,
  language: s.language,
  phoneNumber: s.phoneNumber,
  signingMethod: s.signingMethod,
  reference: s.reference,
});

export function getSigningMethodLabel(signingMethod: SigningMethod | null): TMessage {
  switch (signingMethod) {
    case SigningMethod.AUTH_EMAIL:
      return { key: "signing.signingMethod.email" };
    case SigningMethod.AUTH_SMS:
      return { key: "signing.signingMethod.sms" };
    case SigningMethod.AUTH_CALL:
      return { key: "signing.signingMethod.authCall" };
    case SigningMethod.BANKID:
      return { key: "common.bankid" };
    case SigningMethod.BANKID_AUTH:
      return { key: "signing.signingMethod.bankidauth" };
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return { key: "signing.signingMethod.swedishbankidauth" };
    case SigningMethod.MITID_DENMARK_AUTH:
      return { key: "signing.signingMethod.danish-mitid-auth" };
    case SigningMethod.BANKID_FINLAND_AUTH:
      return { key: "common.bankid-finland" };
    case SigningMethod.HANDWRITING:
      return { key: "signing.signingMethod.handwriting" };
    default:
      return { key: "signatures.send-documents-for-signing.select-method" };
  }
}

export interface Amount {
  currency: string;
  value: number;
}

export type TaxAssessmentValue = {
  assessmentvalueId: string;
  assessmentValue: Amount;
  year: number;
};

export type ShareValuationInformation = {
  value: Amount;
  date: string;
  numberOfShares: number;
  totalValue: Amount;
  shareTypeCode?: ShareTypeCode;
  companyTotalValue: Amount;
};

export type FairValue = {
  fairValueId: string;
  fairValue: Amount;
  shareTypeId: number;
  fairValueDate: string;
};

// noinspection JSUnusedGlobalSymbols
export enum PortfolioTabStatus {
  ACTIVE = "ACTIVE",
  STARRED = "STARRED",
  NOT_CONNECTED = "NOT_CONNECTED",
}

export type UserItem = {
  externalId: string;
  name: string;
  email?: string;
  lastLogin?: string;
  orgNo?: string;
};

export type Portfolio = {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  ownerId: string;
  createdEmail: string;
};

export type CompanyShareholderContact = {
  id: string;
  name: string;
  title?: string;
  email: string;
  phoneNumber?: PhoneNumber;
  linkedin?: string;
  imageReference?: string;
};

export type SsoProviderSettings = {
  domains: string[];
  requireBankIdOnInitialSetup: boolean;
};

export type EnterpriseAccount = {
  id: string;
  name: string;
  internalReferenceName?: string;
  status: EnterpriseAccountStatus;
  billingFrequency: BillingFrequency;
  subscriptionLimits: SubscriptionLimit[];
  startTime: string;
  endTime?: string | null;
  personInfo: PersonInfo;
  primaryCompanyOrgNo: string;
  metadata: EnterpriseAccountMetaData;
  ssoSettings: Record<string, SsoProviderSettings>;
  accountAdminIds: string[];
  features: EnterpriseAccountFeature[];
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
  accessRequestRecipients: AccessRequestRecipients;
  nationalIdMaskingPolicy: NationalIdMaskingPolicy;
  requireSsoForAllUsers?: boolean;
  trankDataSharing: boolean;
  accountAdminsCanUpdateSubscriptions: boolean;
};

export type EnterpriseAccountFeature = {
  enterpriseAccountFeatureId: string;
  enterpriseAccountId: string;
  type: EnterpriseAccountFeatureType;
  status: EnterpriseAccountFeatureStatus;
  selectedCompanyIds: string[];
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
};

export enum EnterpriseAccountFeatureType {
  FINANCIAL_INSTRUMENT_OPTIONS = "FINANCIAL_INSTRUMENT_OPTIONS",
  COMMITTED_CAPITAL = "COMMITTED_CAPITAL",
  ENTITY_DATA = "ENTITY_DATA",
  SHAREHOLDER_REGISTER_ARCHIVE = "SHAREHOLDER_REGISTER_ARCHIVE",
  SHAREPOINT = "SHAREPOINT",
  TEMPLATES = "TEMPLATES",
  INCORPORATION = "INCORPORATION",
  PORTFOLIO_SEARCH = "PORTFOLIO_SEARCH",
  PORTFOLIO_REPORTS = "PORTFOLIO_REPORTS",
}

export enum EnterpriseAccountFeatureStatus {
  DISABLED = "DISABLED",
  ENABLED_FOR_ALL_COMPANIES = "ENABLED_FOR_ALL_COMPANIES",
  ENABLED_FOR_SELECTED_COMPANIES = "ENABLED_FOR_SELECTED_COMPANIES",
}

export const hasAccountFeature = (
  features: EnterpriseAccountFeature[],
  type: EnterpriseAccountFeatureType,
  companyId: string,
): boolean =>
  features.some((feature) => {
    if (feature.type !== type) return false;
    switch (feature.status) {
      case EnterpriseAccountFeatureStatus.DISABLED:
        return false;
      case EnterpriseAccountFeatureStatus.ENABLED_FOR_ALL_COMPANIES:
        return true;
      case EnterpriseAccountFeatureStatus.ENABLED_FOR_SELECTED_COMPANIES:
        return feature.selectedCompanyIds.includes(companyId);
    }
  });

export const accountFeatureLabels: { [key in EnterpriseAccountFeatureType]: TMessage } = {
  [EnterpriseAccountFeatureType.FINANCIAL_INSTRUMENT_OPTIONS]: {
    key: "enterprise-account.feature.financial-instrument-option",
  },
  [EnterpriseAccountFeatureType.COMMITTED_CAPITAL]: {
    key: "enterprise-account.feature.committed-capital",
  },
  [EnterpriseAccountFeatureType.ENTITY_DATA]: {
    key: "enterprise-account.feature.entity-data",
  },
  [EnterpriseAccountFeatureType.SHAREHOLDER_REGISTER_ARCHIVE]: {
    key: "enterprise-account.feature.shareholder-register-archive",
  },
  [EnterpriseAccountFeatureType.SHAREPOINT]: {
    key: "enterprise-account.feature.sharepoint",
  },
  [EnterpriseAccountFeatureType.TEMPLATES]: {
    key: "enterprise-account.feature.templates",
  },
  [EnterpriseAccountFeatureType.INCORPORATION]: {
    key: "enterprise-account.feature.incorporation",
  },
  [EnterpriseAccountFeatureType.PORTFOLIO_SEARCH]: {
    key: "enterprise-account.feature.portfolio-search",
  },
  [EnterpriseAccountFeatureType.PORTFOLIO_REPORTS]: {
    key: "enterprise-account.feature.portfolio-reports",
  },
};

export const accountFeatureStatusLabels: {
  [key in EnterpriseAccountFeatureStatus]: TMessageKey;
} = {
  [EnterpriseAccountFeatureStatus.DISABLED]: "enterprise-account.feature.status.disabled",
  [EnterpriseAccountFeatureStatus.ENABLED_FOR_ALL_COMPANIES]:
    "enterprise-account.feature.status.all-companies",
  [EnterpriseAccountFeatureStatus.ENABLED_FOR_SELECTED_COMPANIES]:
    "enterprise-account.feature.status.selected-companies",
};

export enum EnterpriseAccountStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum BillingFrequency {
  ANNUALLY = "ANNUALLY",
  QUARTERLY = "QUARTERLY",
  MONTHLY = "MONTHLY",
}

export const billingFrequencyLabels: { [key in BillingFrequency]: TMessageKey } = {
  [BillingFrequency.ANNUALLY]: "enterprise-account.billing-frequency.annually",
  [BillingFrequency.QUARTERLY]: "enterprise-account.billing-frequency.quarterly",
  [BillingFrequency.MONTHLY]: "enterprise-account.billing-frequency.monthly",
};

export type EnterpriseAccountMetaData = {
  keyAccountManager?: string | null;
  hubspotLink?: string | null;
  tripletexLink?: string | null;
};

export type EnterpriseAccountStats = {
  account: EnterpriseAccount;
  subscriptionStats: AccountSubscriptionStats[];
  nextBillingDate?: string;
};

export type UserRole = { type: "admin" } | { type: "customRole"; roleId: string; roleName: string };

export type EnterpriseAccountUser = {
  id: string;
  name: string;
  email: string;
  lastSeen?: string;
  companies: CompanyItem[];
  role: UserRole;
};

export type EnterpriseAccountDetails = {
  account: EnterpriseAccount;
  nextBillingDate?: string;
  subscriptionStats: AccountSubscriptionStats[];
  companyDetails: AccountCompanyDetails[];
  products: ProductDescription[];
  eventLogs: EventLogItem[];
  previousInvoiceCounts?: Record<string, number>;
  customRoleItems: CustomRoleItem[];
  users: EnterpriseAccountUser[];
};

export type DecisionTypeCount = {
  decisionType: DecisionType;
  count: number;
};

export type EnterpriseAccountStatsDetails = {
  totalDraftDecisions: number;
  draftDecisionTypeCounts: DecisionTypeCount[];
  totalApprovedDecisions: number;
  approvedDecisionTypeCounts: DecisionTypeCount[];
  shareholderRegisterStatementCount: number;
};

export type EventLogItem = { id: number; timestamp: string } & (
  | AddToEnterpriseAccountItem
  | CreateEnterpriseAccountItem
  | RemoveFromEnterpriseAccountItem
  | SigningCountItem
  | BRSubmissionCountItem
  | InvoiceItem
);

export type AddToEnterpriseAccountItem = {
  type: "addToEnterpriseAccount";
  user: string;
  companies: CompanyItem[];
  productName: string;
};

export type CreateEnterpriseAccountItem = {
  type: "createEnterpriseAccountItem";
  user: string;
  billingFrequency: BillingFrequency;
  startTime: string;
  subscriptionLimits: SubscriptionLimit[];
};

export type RemoveFromEnterpriseAccountItem = {
  type: "removeEnterpriseAccountItem";
  user: string;
  companies: CompanyItem[];
};

export type SigningCountItem = {
  type: "signingCountItem";
  numberOfSignedDocuments: number;
};

export type BRSubmissionCountItem = {
  type: "brSubmissionCountItem";
  count: number;
};

export type InvoiceItem = {
  type: "invoiceItem";
  employeeName: string;
  amount: Amount;
  lines: InvoiceLineItem[];
};

export type InvoiceLineItem = {
  periodFrom: string;
  periodTo: string;
  baseProductName: string | null;
  count: number;
  description: string | null;
};

export type SubscriptionLimit = {
  productId: string;
  limit: number;
};

export type AdminItem = {
  id: string;
  name: string;
  companies: CompanyItem[];
  email: string;
  loginMethod?: LoginMethod;
  lastSeen?: string;
};

// noinspection JSUnusedGlobalSymbols
export enum LoginMethod {
  BANKID = "BANKID",
  USERNAME = "USERNAME",
  MICROSOFT = "MICROSOFT",
}

export type AccountSubscriptionStats = {
  enterpriseAccountId: string;
  productId: string;
  subscriptionCount: number;
};

export type AccountCompanyDetails = {
  companyId: string;
  orgNo: string;
  companyName: string;
  productId?: string;
  shareholderCount: number;
  portfolioId?: string;
  portfolioName?: string;
  organisationType?: string;
  activated: string;
  lastEvent: string;
};

export const productNames = [
  "shareholder_register",
  "digital_company",
  "enterprise",
  "pro",
  "basic",
  "ownership_register",
  "partnership_register",
  "foundation",
] as const;

export type ProductName = (typeof productNames)[number];

const legacyProductNames: ReadonlySet<string> = new Set<ProductName>(["pro", "basic"]);

export function isLegacyProduct(productName: string): boolean {
  return legacyProductNames.has(productName);
}

export type ImageProps = {
  src: string;
  style?: CSSProperties;
};
